import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/core/AuthContext';
import type { UserRole } from '../types';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: UserRole;
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, userData, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
          <p className="mt-4 text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!userData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
          <p className="mt-4 text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Check if user needs to complete onboarding (clients only)
  const onboardingRoutes = ['/onboarding', '/book-intro'];
  const isOnboardingRoute = onboardingRoutes.includes(location.pathname);
  const fromOnboarding = (location.state as any)?.fromOnboarding === true;
  
  if (
    userData.role === 'client' &&
    !userData.onboardingComplete &&
    !isOnboardingRoute &&
    !fromOnboarding
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  // Intro session is required before clients can access other routes
  if (
    userData.role === 'client' &&
    userData.onboardingComplete === true &&
    userData.introSessionBooked !== true &&
    location.pathname !== '/book-intro'
  ) {
    return <Navigate to="/book-intro" replace />;
  }

  if (requiredRole && userData.role !== requiredRole) {
    if (userData.role === 'trainer') {
      return <Navigate to="/trainer" replace />;
    } else if (userData.role === 'client') {
      return <Navigate to="/client" replace />;
    } else {
      return <Navigate to="/login" replace />;
    }
  }

  return <>{children}</>;
}
